import { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { LuPencil } from "react-icons/lu";
import { RiDeleteBinLine } from "react-icons/ri";
import ReactQuill from "react-quill-new";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Input from "../../../components/input";
import LogsComments from "../../../components/LogsComments";
import UserAvatar from "../../../components/UserAvatar";
import { apiService } from "../../../services/apiService";
import "../../../styles/Access.css";
import "../../../styles/DriverPayments.css";
import "../../../styles/DriverSafety.css";
import "../../../styles/Input.css";
import "../../../styles/Issue.css";
import "../../../styles/Table.css";
import "../../../styles/Trip.css";
import "../../../styles/TripCostEstimation.css";
import {
  AppMainModules,
  FinancePermission,
  FinanceSubModules,
} from "../../../utils/app-constant";
import { formats, hasPermission, modules } from "../../../utils/helper";
import { approvedIcon, pendingIcon } from "../../../utils/svgs";
import { errorNotify, successNotify } from "../../../utils/toast";
import SpinnerLoader from "../../../utils/SpinnerLoader";
import {
  buildDriverPaymentRecordFromApi,
  computeDriverPaymentTotals,
  formatAmount,
} from "./driverPayments.utils";

const PAYMENT_TABS = [
  { key: "overview", label: "Overview" },
  { key: "allowances", label: "Allowances" },
  { key: "deductions", label: "Deductions" },
  { key: "violations", label: "Violations" },
  { key: "activity", label: "Activity" },
];

const safeArray = (value) => (Array.isArray(value) ? value : []);
const normalizeTextValue = (value) => String(value || "").trim().toLowerCase();
const toNumber = (value, fallback = 0) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const renderFieldLabel = (label, required = true) => (
  <span className="assets_vehicle_field_label">
    <span>{label}</span>
    {required ? <span className="assets_vehicle_field_required">*</span> : null}
  </span>
);

const extractDriverPaymentHistoryItems = (response = {}) => {
  const responseData = response?.data || response || {};

  if (Array.isArray(responseData?.results)) return responseData.results;
  if (Array.isArray(responseData?.history)) return responseData.history;
  if (Array.isArray(responseData?.comments) || Array.isArray(responseData?.logs)) {
    return [...safeArray(responseData.comments), ...safeArray(responseData.logs)];
  }
  if (Array.isArray(response?.results)) return response.results;
  if (Array.isArray(response?.history)) return response.history;
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(response)) return response;
  return [];
};

const sortHistoryItems = (items = []) =>
  [...safeArray(items)].sort((firstItem, secondItem) => {
    const firstTime = new Date(
      firstItem?.created_at ||
      firstItem?.updated_at ||
      firstItem?.comments?.created_at ||
      firstItem?.logs?.created_at ||
      0
    ).getTime();
    const secondTime = new Date(
      secondItem?.created_at ||
      secondItem?.updated_at ||
      secondItem?.comments?.created_at ||
      secondItem?.logs?.created_at ||
      0
    ).getTime();

    return secondTime - firstTime;
  });

const getResolvedLocationRecord = (locationRecord) => {
  if (!locationRecord || typeof locationRecord !== "object") {
    return null;
  }

  if (locationRecord?.paymentId || locationRecord?.driverName || locationRecord?.rawData) {
    return locationRecord;
  }

  return buildDriverPaymentRecordFromApi(locationRecord);
};

const mergeDriverPaymentRecord = (primaryRecord, fallbackRecord = null) => {
  if (!primaryRecord) return fallbackRecord;
  if (!fallbackRecord) return primaryRecord;

  return {
    ...fallbackRecord,
    ...primaryRecord,
    driverName:
      primaryRecord?.driverName && primaryRecord.driverName !== "-"
        ? primaryRecord.driverName
        : fallbackRecord?.driverName || primaryRecord?.driverName || "-",
    driverImage: primaryRecord?.driverImage || fallbackRecord?.driverImage || "",
    historyItems:
      safeArray(primaryRecord?.historyItems).length > 0
        ? primaryRecord.historyItems
        : safeArray(fallbackRecord?.historyItems),
    activityLog:
      safeArray(primaryRecord?.activityLog).length > 0
        ? primaryRecord.activityLog
        : safeArray(fallbackRecord?.activityLog),
  };
};

const DriverPaymentDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const canUpdatePayments = hasPermission({
    permissionSlug: FinancePermission.Update,
    menuSlug: FinanceSubModules.DriverPayments,
    moduleSlug: AppMainModules.finance,
  });

  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [showAllowanceForm, setShowAllowanceForm] = useState(false);
  const [newAllowanceTitle, setNewAllowanceTitle] = useState("");
  const [newAllowanceAmount, setNewAllowanceAmount] = useState("");
  const [newAllowanceError, setNewAllowanceError] = useState("");
  const [allowanceMutationLoading, setAllowanceMutationLoading] = useState(false);
  const [editingAllowanceId, setEditingAllowanceId] = useState(null);
  const [editingAllowanceTitle, setEditingAllowanceTitle] = useState("");
  const [editingAllowanceAmount, setEditingAllowanceAmount] = useState("");
  const [editingAllowanceError, setEditingAllowanceError] = useState("");
  const [deletingAllowanceId, setDeletingAllowanceId] = useState(null);
  const [showDeductionForm, setShowDeductionForm] = useState(false);
  const [newDeductionReason, setNewDeductionReason] = useState("");
  const [newDeductionAmount, setNewDeductionAmount] = useState("");
  const [newDeductionError, setNewDeductionError] = useState("");
  const [editingDeductionId, setEditingDeductionId] = useState(null);
  const [editingDeductionReason, setEditingDeductionReason] = useState("");
  const [editingDeductionAmount, setEditingDeductionAmount] = useState("");
  const [editingDeductionError, setEditingDeductionError] = useState("");
  const [deductionMutationLoading, setDeductionMutationLoading] = useState(false);
  const [deletingDeductionId, setDeletingDeductionId] = useState(null);

  const resetCreateAllowanceState = useCallback(() => {
    setShowAllowanceForm(false);
    setNewAllowanceTitle("");
    setNewAllowanceAmount("");
    setNewAllowanceError("");
  }, []);

  const resetEditAllowanceState = useCallback(() => {
    setEditingAllowanceId(null);
    setEditingAllowanceTitle("");
    setEditingAllowanceAmount("");
    setEditingAllowanceError("");
  }, []);

  const resetCreateDeductionState = useCallback(() => {
    setShowDeductionForm(false);
    setNewDeductionReason("");
    setNewDeductionAmount("");
    setNewDeductionError("");
  }, []);

  const resetEditDeductionState = useCallback(() => {
    setEditingDeductionId(null);
    setEditingDeductionReason("");
    setEditingDeductionAmount("");
    setEditingDeductionError("");
  }, []);

  const fetchRecord = useCallback(async () => {
    setLoading(true);

    const locationRecord = getResolvedLocationRecord(location.state?.record);
    if (locationRecord) {
      setRecord(locationRecord);
    }

    if (!id) {
      setRecord(locationRecord);
      setLoading(false);
      return;
    }

    try {
      const response = await apiService.getDriverPaymentDetail(id);
      const responseData = response?.data || response || null;
      const detailRecord = responseData ? buildDriverPaymentRecordFromApi(responseData) : null;
      const mergedRecord = mergeDriverPaymentRecord(detailRecord, locationRecord);

      setRecord(mergedRecord);
    } catch (error) {
      setRecord(locationRecord || null);
      errorNotify(error || "Failed to fetch driver payment details");
    } finally {
      setLoading(false);
    }
  }, [id, location.state]);

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      if (!isMounted) return;
      await fetchRecord();
    };

    run();

    return () => {
      isMounted = false;
    };
  }, [fetchRecord]);

  const totals = useMemo(() => computeDriverPaymentTotals(record || {}), [record]);
  const driverMutationDriverId = useMemo(() => {
    const candidateId =
      record?.driverRecordId ??
      record?.rawData?.driver?.id ??
      record?.rawData?.driver_id ??
      0;
    const normalizedValue = Number(candidateId);
    return Number.isFinite(normalizedValue) && normalizedValue > 0 ? normalizedValue : 0;
  }, [record]);
  const driverHistoryId = useMemo(() => {
    const candidateId =
      record?.driverRecordId ??
      record?.rawData?.driver?.id ??
      record?.rawData?.driver_id ??
      record?.rawData?.driver?.driver_info_id ??
      0;
    const normalizedValue = Number(candidateId);
    return Number.isFinite(normalizedValue) && normalizedValue > 0 ? normalizedValue : 0;
  }, [record]);
  const canUseDriverHistoryApi = driverHistoryId > 0;

  useEffect(() => {
    setHistoryItems([]);
  }, [driverHistoryId, record?.id]);

  useEffect(() => {
    resetCreateAllowanceState();
    resetEditAllowanceState();
    resetCreateDeductionState();
    resetEditDeductionState();
  }, [
    record?.id,
    resetCreateAllowanceState,
    resetEditAllowanceState,
    resetCreateDeductionState,
    resetEditDeductionState,
  ]);

  const fetchHistoryItemsByType = useCallback(
    async (historyType) => {
      const historyTypeCandidates =
        historyType === "comment" ? ["comment", "comments"] : [historyType];

      for (const candidateType of historyTypeCandidates) {
        try {
          const response = await apiService.getDriverPaymentHistory({
            driver_info_id: driverHistoryId,
            history_type: candidateType,
          });
          const extractedItems = extractDriverPaymentHistoryItems(response);

          if (extractedItems.length > 0 || historyTypeCandidates.length === 1) {
            return extractedItems;
          }
        } catch (error) {
          if (candidateType === historyTypeCandidates[historyTypeCandidates.length - 1]) {
            throw error;
          }
        }
      }

      return [];
    },
    [driverHistoryId]
  );

  const handleFetchDriverPaymentHistory = useCallback(
    async (historyType = "all") => {
      if (!canUseDriverHistoryApi) return [];

      setHistoryLoading(true);

      try {
        let nextHistoryItems = [];

        if (historyType === "all") {
          const [commentItems, logItems] = await Promise.all([
            fetchHistoryItemsByType("comment").catch(() => []),
            fetchHistoryItemsByType("logs").catch(() => []),
          ]);
          nextHistoryItems = sortHistoryItems([...commentItems, ...logItems]);
        } else {
          nextHistoryItems = sortHistoryItems(await fetchHistoryItemsByType(historyType));
        }

        setHistoryItems(nextHistoryItems);
        return nextHistoryItems;
      } catch (error) {
        errorNotify(error || "Failed to fetch driver payment history");
        setHistoryItems([]);
        return [];
      } finally {
        setHistoryLoading(false);
      }
    },
    [canUseDriverHistoryApi, driverHistoryId]
  );

  const handleSubmitDriverPaymentComment = useCallback(
    async ({ comment, parent_id, mention_user_ids }) => {
      if (!canUseDriverHistoryApi || !record?.id) return;

      setCommentSubmitting(true);

      try {
        await apiService.commentOnDriverPayment({
          driver_payment_id: Number(record.id) || record.id,
          driver_info_id: driverHistoryId,
          comment,
          parent_id,
          mention_user_ids,
        });
      } catch (error) {
        errorNotify(error || "Failed to send driver payment comment");
        throw error;
      } finally {
        setCommentSubmitting(false);
      }
    },
    [canUseDriverHistoryApi, driverHistoryId, record?.id]
  );

  const handleCreateAllowance = useCallback(async () => {
    if (!record?.id || !driverMutationDriverId) {
      setNewAllowanceError("Driver payment detail is incomplete");
      return;
    }

    if (!String(newAllowanceTitle || "").trim()) {
      setNewAllowanceError("Allowance title is required");
      return;
    }

    if (toNumber(newAllowanceAmount) <= 0) {
      setNewAllowanceError("Allowance amount must be greater than 0");
      return;
    }

    setAllowanceMutationLoading(true);

    try {
      await apiService.addDriverPaymentAllowance({
        driver_payment_id: Number(record.id) || record.id,
        driver_id: driverMutationDriverId,
        amount: toNumber(newAllowanceAmount),
        type: String(newAllowanceTitle).trim(),
      });
      await fetchRecord();
      resetCreateAllowanceState();
      successNotify("Allowance added successfully");
    } catch (error) {
      setNewAllowanceError(String(error || "Failed to add allowance"));
      errorNotify(error || "Failed to add allowance");
    } finally {
      setAllowanceMutationLoading(false);
    }
  }, [
    driverMutationDriverId,
    fetchRecord,
    newAllowanceAmount,
    newAllowanceTitle,
    record?.id,
    resetCreateAllowanceState,
  ]);

  const startEditingAllowance = useCallback(
    (allowance) => {
      resetCreateAllowanceState();
      setEditingAllowanceId(allowance?.id ?? null);
      setEditingAllowanceTitle(
        String(allowance?.title || allowance?.type || allowance?.name || "").trim()
      );
      setEditingAllowanceAmount(String(toNumber(allowance?.amount, 0) || ""));
      setEditingAllowanceError("");
    },
    [resetCreateAllowanceState]
  );

  const handleUpdateAllowance = useCallback(
    async (allowance) => {
      const allowanceId = allowance?.id ?? editingAllowanceId;
      const nextAmount = toNumber(editingAllowanceAmount, -1);
      const allowanceType = String(editingAllowanceTitle || allowance?.type || allowance?.title || "").trim();

      if (!allowanceId || !allowanceType || !record?.id || !driverMutationDriverId) {
        setEditingAllowanceError("Allowance detail is incomplete");
        return;
      }

      if (nextAmount <= 0) {
        setEditingAllowanceError("Allowance amount must be greater than 0");
        return;
      }

      setAllowanceMutationLoading(true);

      try {
        await apiService.updateDriverPaymentAllowance({
          id: allowanceId,
          amount: nextAmount,
          type: allowanceType,
          reason: allowanceType,
        });
        await fetchRecord();
        resetEditAllowanceState();
        successNotify("Allowance updated successfully");
      } catch (error) {
        setEditingAllowanceError(String(error || "Failed to update allowance"));
        errorNotify(error || "Failed to update allowance");
      } finally {
        setAllowanceMutationLoading(false);
      }
    },
    [
      driverMutationDriverId,
      editingAllowanceAmount,
      editingAllowanceId,
      editingAllowanceTitle,
      fetchRecord,
      record?.id,
      resetEditAllowanceState,
    ]
  );

  const handleDeleteAllowance = useCallback(
    async (allowanceId) => {
      if (!allowanceId) return;

      setDeletingAllowanceId(allowanceId);

      try {
        await apiService.deleteDriverPaymentAllowance(allowanceId);
        await fetchRecord();
        if (editingAllowanceId === allowanceId) {
          resetEditAllowanceState();
        }
        successNotify("Allowance deleted successfully");
      } catch (error) {
        errorNotify(error || "Failed to delete allowance");
      } finally {
        setDeletingAllowanceId(null);
      }
    },
    [editingAllowanceId, fetchRecord, resetEditAllowanceState]
  );

  const handleCreateDeduction = useCallback(async () => {
    if (!record?.id || !driverMutationDriverId) {
      setNewDeductionError("Driver payment detail is incomplete");
      return;
    }

    if (!String(newDeductionReason || "").trim()) {
      setNewDeductionError("Deduction reason is required");
      return;
    }

    if (toNumber(newDeductionAmount) <= 0) {
      setNewDeductionError("Deduction amount must be greater than 0");
      return;
    }

    setDeductionMutationLoading(true);

    try {
      await apiService.addDriverPaymentDeduction({
        driver_payment_id: Number(record.id) || record.id,
        driver_id: driverMutationDriverId,
        amount: toNumber(newDeductionAmount),
        reason: String(newDeductionReason).trim(),
      });
      await fetchRecord();
      resetCreateDeductionState();
      successNotify("Deduction added successfully");
    } catch (error) {
      setNewDeductionError(String(error || "Failed to add deduction"));
      errorNotify(error || "Failed to add deduction");
    } finally {
      setDeductionMutationLoading(false);
    }
  }, [
    driverMutationDriverId,
    fetchRecord,
    newDeductionAmount,
    newDeductionReason,
    record?.id,
    resetCreateDeductionState,
  ]);

  const startEditingDeduction = useCallback(
    (deduction) => {
      resetCreateDeductionState();
      setEditingDeductionId(deduction?.id ?? null);
      setEditingDeductionReason(String(deduction?.title || deduction?.reason || "").trim());
      setEditingDeductionAmount(String(toNumber(deduction?.amount, 0) || ""));
      setEditingDeductionError("");
    },
    [resetCreateDeductionState]
  );

  const handleUpdateDeduction = useCallback(
    async (deduction) => {
      const deductionId = deduction?.id ?? editingDeductionId;

      if (!deductionId || !record?.id || !driverMutationDriverId) {
        setEditingDeductionError("Deduction detail is incomplete");
        return;
      }

      if (!String(editingDeductionReason || "").trim()) {
        setEditingDeductionError("Deduction reason is required");
        return;
      }

      if (toNumber(editingDeductionAmount) <= 0) {
        setEditingDeductionError("Deduction amount must be greater than 0");
        return;
      }

      setDeductionMutationLoading(true);

      try {
        await apiService.updateDriverPaymentDeduction({
          id: deductionId,
          driver_payment_id: Number(record.id) || record.id,
          driver_id: driverMutationDriverId,
          amount: toNumber(editingDeductionAmount),
          reason: String(editingDeductionReason).trim(),
        });
        await fetchRecord();
        resetEditDeductionState();
        successNotify("Deduction updated successfully");
      } catch (error) {
        setEditingDeductionError(String(error || "Failed to update deduction"));
        errorNotify(error || "Failed to update deduction");
      } finally {
        setDeductionMutationLoading(false);
      }
    },
    [
      driverMutationDriverId,
      editingDeductionAmount,
      editingDeductionId,
      editingDeductionReason,
      fetchRecord,
      record?.id,
      resetEditDeductionState,
    ]
  );

  const handleDeleteDeduction = useCallback(
    async (deductionId) => {
      if (!deductionId) return;

      setDeletingDeductionId(deductionId);

      try {
        await apiService.deleteDriverPaymentDeduction(deductionId);
        await fetchRecord();
        if (editingDeductionId === deductionId) {
          resetEditDeductionState();
        }
        successNotify("Deduction deleted successfully");
      } catch (error) {
        errorNotify(error || "Failed to delete deduction");
      } finally {
        setDeletingDeductionId(null);
      }
    },
    [editingDeductionId, fetchRecord, resetEditDeductionState]
  );

  const overviewFields = useMemo(
    () => {
      const findAllowanceAmount = (keywords = []) =>
        safeArray(record?.allowances).find((allowance) => {
          const haystack = normalizeTextValue(
            `${allowance?.title || ""} ${allowance?.type || ""}`
          );
          return keywords.some((keyword) => haystack.includes(keyword));
        })?.amount || 0;

      return [
        { id: "base-salary", title: "Base Salary", amount: totals.baseSalary },
        {
          id: "food-allowance",
          title: "Food Allowance",
          amount: findAllowanceAmount(["food"]),
        },
        {
          id: "trip-incentive",
          title: "Trip Incentive",
          amount: findAllowanceAmount(["trip incentive", "trip_incentive", "trip"]),
        },
        {
          id: "total-deductions",
          title: "Total Deductions",
          amount: totals.totalDeductions,
        },
      ];
    },
    [record?.allowances, totals.baseSalary, totals.totalDeductions]
  );

  const isPaid = String(record?.status || "").toLowerCase() === "paid";

  if (loading) {
    return <SpinnerLoader />;
  }

  if (!record) {
    return (
      <div className="driver_payments_page trip_cost_estimation_page">
        <Breadcrumbs values={["Finance", "Driver Payments", "Payment Detail"]} />
        <div className="page_container">
          <div className="table_no_records">Record not found</div>
        </div>
      </div>
    );
  }

  return (
    <div className="driver_payments_page trip_cost_estimation_page driver_payment_details_page">
      <Breadcrumbs values={["Finance", "Driver Payments", "Payment Detail"]} />

      <div className="page_header access_view">
        <h2>Payment Details</h2>

        <div className="driver_payment_header_actions">
          <button type="button" className="cancel_btn" onClick={() => navigate("/driver-payments")}>
            Cancel
          </button>
        </div>
      </div>

      <div className="page_container">
        <Row className="driver_page_layout trip_cost_estimation_details_layout">
          <Col lg={9}>
            <div className="driver_payment_detail_bar">
              <span>Payment ID:</span>
              <strong>{record.paymentId}</strong>
            </div>

            <div className="driver_payment_hero">
              <div className="driver_payment_hero_identity">
                <UserAvatar
                  username={record.driverName}
                  image={record.driverImage}
                  size={58}
                  fontSize="18px"
                  marginRight="0px"
                />
                <div>
                  <h3>{record.driverName}</h3>
                  <p>{record.monthLabel}</p>
                </div>
                <span
                  className={`driver_payments_status ${isPaid ? "is_paid" : "is_pending"}`}
                >
                  <span className="driver_payments_status_icon">
                    {isPaid ? approvedIcon : pendingIcon}
                  </span>
                  <span>{isPaid ? "Paid" : "Pending"}</span>
                </span>
              </div>

              <div className="driver_payment_hero_summary">
                <div>
                  <h4>Net Pay Salary (PKR)</h4>
                  <p>{`This Month - ${record.monthLabel}`}</p>
                </div>
                <strong>{formatAmount(totals.netPay)}</strong>
              </div>
            </div>

            <div className="driver_payment_tab_row">
              {PAYMENT_TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={activeTab === tab.key ? "is_active" : ""}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === "overview" && (
              <div className="trip_information_main_div driver_form_shell trip_cost_estimation_detail_shell">
                <div className="driver_form_content">
                  <div className="driver_form_section">
                    <div className="driver_form_section_header">
                      <h3>Salary Breakdown</h3>
                      <p>Main information for this part record.</p>
                    </div>
                    <Row>
                      {overviewFields.map((field) => (
                        <Col md={6} key={field.id}>
                          <Input
                            label={renderFieldLabel(field.title, false)}
                            type="number"
                            placeholder="0"
                            name={field.id}
                            value={field.amount}
                            onChange={() => {}}
                            showExtraText
                            extraText="PKR"
                            disabled
                            required={false}
                          />
                        </Col>
                      ))}
                    </Row>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "allowances" && (
              <div className="trip_information_main_div driver_form_shell trip_cost_estimation_detail_shell">
                <div className="driver_form_content">
                  <div className="driver_form_section">
                    <div className="driver_form_section_header">
                      <h3>Allowance Breakdown</h3>
                      <p>Main information for this part record.</p>
                    </div>

                    {safeArray(record.allowances).length ? (
                      <div className="driver_payment_deduction_list">
                        {safeArray(record.allowances).map((allowance) => (
                          <div key={allowance.id} className="driver_payment_deduction_item">
                            {editingAllowanceId === allowance.id ? (
                              <div className="driver_payment_inline_form" style={{ width: "100%" }}>
                                <Row>
                                  <Col md={6}>
                                    <Input
                                      label={renderFieldLabel("Allowance Title")}
                                      type="text"
                                      placeholder="Write something"
                                      name={`edit-allowance-title-${allowance.id}`}
                                      value={editingAllowanceTitle}
                                      onChange={(event) => {
                                        setEditingAllowanceTitle(event?.target?.value || "");
                                        setEditingAllowanceError("");
                                      }}
                                      error={editingAllowanceError}
                                      touched={Boolean(editingAllowanceError)}
                                    />
                                  </Col>
                                  <Col md={6}>
                                    <Input
                                      label={renderFieldLabel("Allowance Amount")}
                                      type="number"
                                      placeholder="00"
                                      name={`edit-allowance-amount-${allowance.id}`}
                                      value={editingAllowanceAmount}
                                      onChange={(event) => {
                                        setEditingAllowanceAmount(event?.target?.value || "");
                                        setEditingAllowanceError("");
                                      }}
                                      error={editingAllowanceError}
                                      touched={Boolean(editingAllowanceError)}
                                      showExtraText
                                      extraText="PKR"
                                    />
                                  </Col>
                                </Row>

                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                  <button
                                    type="button"
                                    className="main_btn driver_payment_save_btn"
                                    onClick={() => handleUpdateAllowance(allowance)}
                                    disabled={allowanceMutationLoading}
                                  >
                                    {allowanceMutationLoading ? "Saving..." : "Update Allowance"}
                                  </button>
                                  <button
                                    type="button"
                                    className="cancel_btn"
                                    onClick={resetEditAllowanceState}
                                    disabled={allowanceMutationLoading}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="driver_payment_entry_main">
                                  <span className="driver_payment_entry_badge">
                                    {allowance.title}
                                  </span>
                                </div>
                                <div className="driver_payment_entry_actions">
                                  <strong className="driver_payment_entry_amount">
                                    {formatAmount(allowance.amount)} PKR
                                  </strong>
                                  {canUpdatePayments ? (
                                    <div className="table_action_btn">
                                      <button
                                        type="button"
                                        className="edit_btn"
                                        title="Edit allowance"
                                        onClick={() => startEditingAllowance(allowance)}
                                        disabled={allowanceMutationLoading || deletingAllowanceId === allowance.id}
                                      >
                                        <LuPencil />
                                      </button>
                                      <button
                                        type="button"
                                        className="delete_btn"
                                        title="Delete allowance"
                                        onClick={() => handleDeleteAllowance(allowance.id)}
                                        disabled={deletingAllowanceId === allowance.id}
                                      >
                                        <RiDeleteBinLine />
                                      </button>
                                    </div>
                                  ) : null}
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="table_no_records">Record not found</div>
                    )}

                    {canUpdatePayments ? (
                      <>
                        {!showAllowanceForm ? (
                          <button
                            type="button"
                            className="driver_payment_add_button"
                            onClick={() => {
                              resetEditAllowanceState();
                              setShowAllowanceForm(true);
                            }}
                            disabled={allowanceMutationLoading}
                          >
                            Add More Allowances
                          </button>
                        ) : (
                          <div className="driver_payment_inline_form">
                            <Row>
                              <Col md={6}>
                                <Input
                                  label={renderFieldLabel("Allowance Title")}
                                  type="text"
                                  placeholder="Write something"
                                  name="allowanceTitle"
                                  value={newAllowanceTitle}
                                  onChange={(event) => {
                                    setNewAllowanceTitle(event?.target?.value || "");
                                    setNewAllowanceError("");
                                  }}
                                  error={newAllowanceError}
                                  touched={Boolean(newAllowanceError)}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label={renderFieldLabel("Allowance Amount")}
                                  type="number"
                                  placeholder="00"
                                  name="allowanceAmount"
                                  value={newAllowanceAmount}
                                  onChange={(event) => {
                                    setNewAllowanceAmount(event?.target?.value || "");
                                    setNewAllowanceError("");
                                  }}
                                  error={newAllowanceError}
                                  touched={Boolean(newAllowanceError)}
                                  showExtraText
                                  extraText="PKR"
                                />
                              </Col>
                            </Row>

                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              <button
                                type="button"
                                className="main_btn driver_payment_save_btn"
                                onClick={handleCreateAllowance}
                                disabled={allowanceMutationLoading}
                              >
                                {allowanceMutationLoading ? "Saving..." : "Save"}
                              </button>
                              <button
                                type="button"
                                className="cancel_btn"
                                onClick={resetCreateAllowanceState}
                                disabled={allowanceMutationLoading}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    ) : null}

                    <div className="driver_payment_total_line">
                      <span>Total Allowances</span>
                      <strong>{formatAmount(totals.totalAllowances)} PKR</strong>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "deductions" && (
              <div className="trip_information_main_div driver_form_shell trip_cost_estimation_detail_shell">
                <div className="driver_form_content">
                  <div className="driver_form_section">
                    <div className="driver_form_section_header">
                      <h3>Applied Deductions</h3>
                      <p>Main information for this part record.</p>
                    </div>

                    <div className="driver_payment_deduction_list">
                      {safeArray(record.deductions).length ? safeArray(record.deductions).map((deduction) => (
                        <div key={deduction.id} className="driver_payment_deduction_item">
                          {editingDeductionId === deduction.id ? (
                            <div className="driver_payment_inline_form" style={{ width: "100%" }}>
                              <Row>
                                <Col md={6}>
                                  <Input
                                    label={renderFieldLabel("Deduction Reason")}
                                    type="text"
                                    placeholder="Write something"
                                    name={`edit-deduction-reason-${deduction.id}`}
                                    value={editingDeductionReason}
                                    onChange={(event) => {
                                      setEditingDeductionReason(event?.target?.value || "");
                                      setEditingDeductionError("");
                                    }}
                                    error={editingDeductionError}
                                    touched={Boolean(editingDeductionError)}
                                  />
                                </Col>
                                <Col md={6}>
                                  <Input
                                    label={renderFieldLabel("Deduction Amount")}
                                    type="number"
                                    placeholder="00"
                                    name={`edit-deduction-amount-${deduction.id}`}
                                    value={editingDeductionAmount}
                                    onChange={(event) => {
                                      setEditingDeductionAmount(event?.target?.value || "");
                                      setEditingDeductionError("");
                                    }}
                                    error={editingDeductionError}
                                    touched={Boolean(editingDeductionError)}
                                    showExtraText
                                    extraText="PKR"
                                  />
                                </Col>
                              </Row>

                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                <button
                                  type="button"
                                  className="main_btn driver_payment_save_btn"
                                  onClick={() => handleUpdateDeduction(deduction)}
                                  disabled={deductionMutationLoading}
                                >
                                  {deductionMutationLoading ? "Saving..." : "Update Deduction"}
                                </button>
                                <button
                                  type="button"
                                  className="cancel_btn"
                                  onClick={resetEditDeductionState}
                                  disabled={deductionMutationLoading}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="driver_payment_entry_main">
                                <span className="driver_payment_entry_badge">
                                  {deduction.title}
                                </span>
                              </div>
                              <div className="driver_payment_entry_actions">
                                <strong className="driver_payment_entry_amount">
                                  {formatAmount(deduction.amount)} PKR
                                </strong>
                                {canUpdatePayments ? (
                                  <div className="table_action_btn">
                                    <button
                                      type="button"
                                      className="edit_btn"
                                      title="Edit deduction"
                                      onClick={() => startEditingDeduction(deduction)}
                                      disabled={deductionMutationLoading || deletingDeductionId === deduction.id}
                                    >
                                      <LuPencil />
                                    </button>
                                    <button
                                      type="button"
                                      className="delete_btn"
                                      title="Delete deduction"
                                      onClick={() => handleDeleteDeduction(deduction.id)}
                                      disabled={deletingDeductionId === deduction.id}
                                    >
                                      <RiDeleteBinLine />
                                    </button>
                                  </div>
                                ) : null}
                              </div>
                            </>
                          )}
                        </div>
                      )) : <div className="table_no_records">Record not found</div>}
                    </div>

                    {canUpdatePayments ? (
                      <>
                        {!showDeductionForm ? (
                          <button
                            type="button"
                            className="driver_payment_add_button"
                            onClick={() => {
                              resetEditDeductionState();
                              setShowDeductionForm(true);
                            }}
                          >
                            Add More Deductions
                          </button>
                        ) : (
                          <div className="driver_payment_inline_form">
                            <Row>
                              <Col md={6}>
                                <Input
                                  label={renderFieldLabel("Deduction Reason")}
                                  type="text"
                                  placeholder="Write something"
                                  name="deductionReason"
                                  value={newDeductionReason}
                                  onChange={(event) => {
                                    setNewDeductionReason(event?.target?.value || "");
                                    setNewDeductionError("");
                                  }}
                                  error={newDeductionError}
                                  touched={Boolean(newDeductionError)}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label={renderFieldLabel("Deduction Amount")}
                                  type="number"
                                  placeholder="00"
                                  name="deductionAmount"
                                  value={newDeductionAmount}
                                  onChange={(event) => {
                                    setNewDeductionAmount(event?.target?.value || "");
                                    setNewDeductionError("");
                                  }}
                                  error={newDeductionError}
                                  touched={Boolean(newDeductionError)}
                                  showExtraText
                                  extraText="PKR"
                                />
                              </Col>
                            </Row>

                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              <button
                                type="button"
                                className="main_btn driver_payment_save_btn"
                                onClick={handleCreateDeduction}
                                disabled={deductionMutationLoading}
                              >
                                {deductionMutationLoading ? "Saving..." : "Apply Deduction"}
                              </button>
                              <button
                                type="button"
                                className="cancel_btn"
                                onClick={resetCreateDeductionState}
                                disabled={deductionMutationLoading}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    ) : null}

                    <div className="driver_payment_total_line">
                      <span>Total Deductions</span>
                      <strong>{formatAmount(totals.totalDeductions)} PKR</strong>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "violations" && (
              <div className="trip_information_main_div driver_form_shell trip_cost_estimation_detail_shell">
                <div className="driver_form_content">
                  <div className="driver_form_section">
                    <div className="driver_form_section_header">
                      <h3>Operational Violations</h3>
                      <p>Violations originate from: Fuel module, Trip module, Compliance logs</p>
                    </div>

                    <div className="driver_payment_violation_list">
                      {safeArray(record.violations).length ? (
                        safeArray(record.violations).map((violation) => (
                          <div key={violation.id} className="driver_payment_violation_item">
                            <div>
                              <h6>{violation.title}</h6>
                              <p>{`Source : ${violation.source}`}</p>
                            </div>
                            <strong>{`${violation.incidents} Incidents`}</strong>
                          </div>
                        ))
                      ) : (
                        <div className="table_no_records">Record not found</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "activity" && (
              <div className="trip_information_main_div driver_form_shell trip_cost_estimation_detail_shell">
                <div className="driver_form_content">
                  <div className="driver_form_section">
                    <div className="driver_form_section_header">
                      <h3>Payment Activity Log</h3>
                      <p>Main information for this part record.</p>
                    </div>

                    <div className="driver_payment_activity_list">
                      {safeArray(record.activityLog).length ? (
                        safeArray(record.activityLog).map((entry) => (
                          <div key={entry.id} className="driver_payment_activity_item">
                            <span className={`driver_payment_activity_dot is_${entry.color}`} />
                            <div>
                              <h6>{entry.title}</h6>
                              <p>{`By: ${entry.by}`}</p>
                              <small>{entry.time || "-"}</small>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="table_no_records">Record not found</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Col>

          <Col lg={3}>
            <div className="service_cost_summary_details driver_details_sidebar trip_cost_estimation_details_sidebar">
              <div className="service_cost_summary mb-3">
                <h6>Payment Summary</h6>
                <p>This Month Financial Breakdown</p>

                <div className="service_item">
                  <div>Base Salary (PKR)</div>
                  <div>{formatAmount(totals.baseSalary)}</div>
                </div>
                <div className="service_item">
                  <div>Allowances (PKR)</div>
                  <div>{formatAmount(totals.totalAllowances)}</div>
                </div>
                <div className="service_item">
                  <div>Deductions (PKR)</div>
                  <div>{formatAmount(totals.totalDeductions)}</div>
                </div>

                <div className="total">
                  <div>Net Pay Salary (PKR)</div>
                  <span>{formatAmount(totals.netPay)}</span>
                </div>
              </div>

              <LogsComments
                key={driverHistoryId || id || "driver-payment-history"}
                historyItems={historyItems}
                onFetchHistory={handleFetchDriverPaymentHistory}
                onSubmitComment={handleSubmitDriverPaymentComment}
                loading={historyLoading || commentSubmitting}
                editorModules={modules}
                editorFormats={formats}
                emptyMessages={{
                  all: "No comments or activity found",
                  comment: "No comments found",
                  logs: "No activity found",
                }}
              />

              <div className="driver_payment_notes_card driver_details_notes_card">
                <div className="parts_general_notes">
                  <h5>General Notes</h5>
                  <p>Add any additional notes or observations here.</p>
                  <ReactQuill
                    placeholder="write notes here..."
                    modules={{ toolbar: false }}
                    theme="snow"
                    value={record.notes || ""}
                    formats={formats}
                    readOnly
                    className="react_quill_input notes_quill_input quill-small-font trip_quill_input"
                  />
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default DriverPaymentDetails;
